<!-- Slide editor toolbar (Phase 3 editor template): Name + Sharing on a
     token card, Full Screen as a tertiary control, Save/Exit as BUI CTAs.
     Save/Exit stay here (not a floating footer) because the fullscreen
     dialog reuses SaveAndExitSlideButtons in its top bar. -->

<template>
<div class="sle-toolbar">
  <div class="sle-toolbar__fields">
    <div class="sle-field">
      <label for="sle-name" class="sle-field__label">Name &#9733;</label>
      <TextField id="sle-name" data-test-id="slide-name" v-model="basicInfo.name" width="26ch" :disabled="isReadOnly" />
    </div>
    <div class="sle-field">
      <label class="sle-field__label">Sharing</label>
      <SelectField
        class="sle-select"
        :options="sharingLevelOptions"
        v-model="basicInfo.sharingLevel"
        :disabled="isReadOnly"
      />
    </div>
  </div>

  <div class="sle-toolbar__actions">
    <FullScreenDialog v-if="!isReadOnly">
      <template #expander>
        <button type="button" class="sle-fullscreen-btn">
          <PhArrowsOut :size="16" weight="bold" />
          Full Screen
        </button>
      </template>
    </FullScreenDialog>
    <SaveAndExitSlideButtons :isReadOnly="isReadOnly" />
  </div>
</div>
<!-- {{ history.length }}
<div v-for="item of history">
  {{ item.basicInfo.name }}
  <br>
</div> -->
<!-- {{ {history, redoHistory} }} -->
</template>

<script lang="ts" setup>
import { PhArrowsOut } from '@phosphor-icons/vue';
import TextField from '@/components/form-fields/TextField.vue';
import SelectField from '@/components/form-fields/SelectField/SelectField.vue';
import FullScreenDialog from './FullScreenDialog.vue';
import useSlide from '@/views/MediaEditorView/SlideEditor/composables/slide';
import SaveAndExitSlideButtons from './SaveAndExitSlideButtons.vue';
import { sharingLevelOptions } from '@/data/options/sharingLevelOptions';

defineProps<{
  isReadOnly: boolean
}>();

const { basicInfo, history, redoHistory } = useSlide();
</script>

<style lang="scss" scoped>
.sle-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
  padding: 0.75rem 1.25rem;
  background: var(--bui-tbl-row-fill, #fffcfd);
  color: var(--bui-tbl-row-text, #2a2969);
  border-radius: var(--bui-tbl-card-radius, 12px);
  box-shadow: var(--bui-tbl-card-shadow, 0 8px 32px rgba(75, 24, 134, 0.16));
}

.sle-toolbar__fields {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  flex-wrap: wrap;
}

.sle-field {
  display: flex;
  align-items: center;
  gap: 0.6rem;

  &__label {
    font-size: 0.875rem;
    font-weight: 700;
    color: var(--bui-tbl-row-text, #2a2a30);
    white-space: nowrap;
  }

  :deep(input[type='text']) {
    color: var(--bui-ter-input-text, #1a1a1e);
    background: var(--bui-ter-input-fill, rgba(0, 0, 0, 0.04));
    border-color: var(--bui-ter-input-border, rgba(0, 0, 0, 0.12));
  }
}

/* Legacy SelectField shell → tokens. */
.sle-select {
  :deep(.styled-select) {
    border-color: var(--bui-ter-input-border, rgba(0, 0, 0, 0.12));
    border-radius: var(--bui-ter-radius, 8px);
    background: var(--bui-ter-input-fill, rgba(0, 0, 0, 0.04));

    select {
      color: var(--bui-ter-input-text, #1a1a1e);
    }
  }
}

.sle-toolbar__actions {
  display: flex;
  flex-direction: row-reverse;
  align-items: center;
  gap: 0.75rem;

  /* Save / Exit legacy variants → BUI CTA tokens (same mapping as the
     editor-template footer). */
  :deep(.button) {
    background-color: var(--bui-cta-pri-fill, #baff5c);
    color: var(--bui-cta-pri-text, #007366);
    border-radius: var(--bui-ter-radius, 8px);
    text-transform: none;
    font-weight: 500;
    box-shadow: none;

    &.secondary,
    &.cta-secondary {
      background-color: var(--bui-cta-sec-fill, #4b1886);
      color: var(--bui-cta-sec-text, #fffcfd);
    }

    &:hover:not(.disabled) {
      filter: brightness(0.96);
    }

    &.secondary:hover:not(.disabled),
    &.cta-secondary:hover:not(.disabled) {
      filter: brightness(1.08);
      background-color: var(--bui-cta-sec-fill, #4b1886);
    }
  }

  /* Dialog expander wrappers hug their control. */
  :deep(> .flex),
  :deep(.flex) {
    display: inline-flex;
    width: auto;
    flex: 0 0 auto;
  }
}

.sle-fullscreen-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem 0.9rem;
  border: 1px solid var(--bui-ter-input-border, rgba(0, 0, 0, 0.12));
  border-radius: var(--bui-ter-radius, 8px);
  background: var(--bui-ter-input-fill, rgba(0, 0, 0, 0.04));
  color: var(--bui-tbl-row-text, #2a2969);
  font-family: inherit;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: var(--bui-tbl-rowbtn-hover-fill, #ececf0);
  }

  &:focus-visible {
    outline: 2px solid var(--bui-chk-checked-fill, #de3da9);
    outline-offset: 2px;
  }
}
</style>
